/**
 * Presets Store (Zustand with persistence)
 *
 * Stores user-defined tool presets: a custom name + a config snapshot for a base tool.
 */

import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

import type { IToolPreset } from '@/interfaces/IStorage';
import { SENSITIVE_CONFIG_KEYS } from '@/lib/storeConstants';

function generatePresetId(): string {
  return `preset-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface PresetState {
  presets: IToolPreset[];

  createPreset: (args: {
    toolId: string;
    name: string;
    config: Record<string, unknown>;
  }) => IToolPreset;

  updatePreset: (presetId: string, patch: Partial<Pick<IToolPreset, 'name' | 'config'>>) => void;
  deletePreset: (presetId: string) => void;

  getPreset: (presetId: string) => IToolPreset | undefined;
  getPresetsForTool: (toolId: string) => IToolPreset[];
  reset: () => void;
}

/**
 * Strip sensitive config keys from a preset config before persisting.
 * Values like HMAC secret keys should never be saved to localStorage.
 */
function stripSensitiveConfig(config: Record<string, unknown>): Record<string, unknown> {
  const cleaned = { ...config };
  for (const key of SENSITIVE_CONFIG_KEYS) {
    delete cleaned[key];
  }
  return cleaned;
}

export const usePresetStore = create<PresetState>()(
  persist(
    (set, get) => ({
      presets: [],

      createPreset: ({ toolId, name, config }) => {
        const now = new Date().toISOString();
        const preset: IToolPreset = {
          id: generatePresetId(),
          toolId,
          name: name.trim() || 'Untitled preset',
          config: { ...(config ?? {}) },
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          presets: [preset, ...state.presets],
        }));

        return preset;
      },

      updatePreset: (presetId, patch) => {
        set((state) => ({
          presets: state.presets.map((p) => {
            if (p.id !== presetId) return p;
            return {
              ...p,
              ...patch,
              name: patch.name !== undefined ? patch.name.trim() || 'Untitled preset' : p.name,
              config: patch.config !== undefined ? { ...(patch.config ?? {}) } : p.config,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      deletePreset: (presetId) => {
        set((state) => ({
          presets: state.presets.filter((p) => p.id !== presetId),
        }));
      },

      getPreset: (presetId) => get().presets.find((p) => p.id === presetId),

      getPresetsForTool: (toolId) => get().presets.filter((p) => p.toolId === toolId),

      reset: () => {
        set({ presets: [] });
      },
    }),
    {
      name: 'swedevtools-presets',
      version: 2,
      migrate: (persisted, version) => {
        const state = { presets: [], ...(persisted as Record<string, unknown>) } as unknown as PresetState;
        // v1→v2: strip sensitive config keys from existing presets
        if (version != null && version < 2) {
          state.presets = state.presets.map((p) => ({
            ...p,
            config: stripSensitiveConfig(p.config ?? {}),
          }));
        }
        return state;
      },
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        presets: state.presets.map((p) => ({
          ...p,
          config: stripSensitiveConfig(p.config),
        })),
      }),
    }
  )
);
